import { useMemo } from "react";

import { roleProviderId } from "@smart/bridge-smokeball-basic";
import { ProgressBar } from "@smart/itops-sb-design-system-dom";
import {
  isNotNullOrUndefined,
  partition,
  toSentenceCase,
  unique,
} from "@smart/itops-utils-basic";

import { hideField, isContactTypeFolder } from "./helpers";
import { PickerCrumbs } from "./picker-crumbs";
import { PickerItem } from "./picker-item";
import { PickerSelection } from "./types";
import { fieldComponentIcons } from "../../constants";
import {
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
  MatterField,
} from "../../types";
import {
  isContactDetailsGroup as checkIsContactDetailsGroup,
  contactTypes,
  hasExistingContactDetailsGroup,
  mapFieldType,
} from "../../utils";

enum RoleFolders {
  Person = "person",
  Company = "company",
  Trust = "trust",
}

export type PickerProps = {
  loading: boolean;
  fixedSelection?: PickerSelection;
  selection: PickerSelection;
  setSelection: (selection: PickerSelection) => void;
  matterTypes: GqlMatterType[];
  layouts: GqlMatterLayout[];
  fields: MatterField[];
  existingMappedFields: string[];
  groupMappedFieldNames?: string[];
  multiSelect?: boolean;
  isLoadingMatterFields: boolean | undefined;
  group?: GqlGroup;
  groups: GqlGroup[];
  allowLayoutContactFields?: boolean;
};

export const Picker = ({
  loading,
  fixedSelection,
  selection,
  setSelection,
  matterTypes,
  layouts,
  fields,
  existingMappedFields,
  groupMappedFieldNames = [],
  multiSelect = true,
  isLoadingMatterFields,
  group,
  groups,
  allowLayoutContactFields = true,
}: PickerProps) => {
  const isContactDetailsGroup = checkIsContactDetailsGroup(group?.values);
  const roleComponentIndex = selection.components.findIndex(
    (c) => c.type === "role",
  );
  const isRoleComponentSelected = roleComponentIndex !== -1;
  const isRoleProviderLayout = selection.layout?.providerId === roleProviderId;
  const isRoleFolder = isRoleProviderLayout || isRoleComponentSelected;

  const isInPersonFolder = (name: string) =>
    name.startsWith(RoleFolders.Person);
  const isInCompanyFolder = (name: string) =>
    name.startsWith(RoleFolders.Company);
  const isInTrustFolder = (name: string) => name.startsWith(RoleFolders.Trust);

  const shouldLayoutBeDisabled = (layout: GqlMatterLayout): boolean => {
    if (!fixedSelection) return false;

    // Current fixed selection is a role provider layout
    if (
      fixedSelection.layout?.providerId === roleProviderId &&
      !fixedSelection.layout?.parentId
    ) {
      // The layout is a sub layout of the fixed selection
      if (layout.parentId && layout.parentId === fixedSelection.layout?.id)
        return false;

      // The layout is the same as the fixed selection
      if (
        fixedSelection.layout?.id &&
        fixedSelection.layout.id === layout.id &&
        layout.providerId === roleProviderId &&
        !layout.parentId
      )
        return false;
    }

    return true;
  };

  const isOrdinaryGroup =
    !!group &&
    group.values.type !== "layoutContact" &&
    group.values.type !== "contactDetails";

  const hasDuplicateContactDetailsGroup = (folder: {
    name: string;
  }): boolean => {
    const contactType = contactTypes.find((type) => type === folder.name);
    if (!contactType) return false;

    return hasExistingContactDetailsGroup({
      groups,
      layoutId: selection.layout?.id,
      contactType,
      excludeGroupUri: group?.uri,
    });
  };

  const isLayoutContactFolderDisabled = (folder: { name: string }) =>
    !allowLayoutContactFields &&
    isRoleComponentSelected &&
    isContactTypeFolder(folder.name);

  const shouldFolderBeDisabled = (folder: { name: string }): boolean => {
    if (hasDuplicateContactDetailsGroup(folder)) return true;

    /**
     * Only allow select either Company and Person fields if it's in a group or selecting a Role field's field.
     *
     * The following data should be checked if a role field has been selected:
     * 1. "selection": for current selection.
     * 2. "fixedSelection": for existing selected fields in the group.
     * 3. "existingMappedFields": for existing mapped fields in the group.
     */

    if (isLayoutContactFolderDisabled(folder)) {
      return true;
    }

    // Check if a Role component selected separately,
    // because a "layoutContact" group will be created automatically, if not exists.
    if (isRoleComponentSelected) {
      const isPersonMappedFieldsSelected =
        selection.fields.some((f) => isInPersonFolder(f.name)) ||
        existingMappedFields.some((name) => isInPersonFolder(name));
      const isCompanyMappedFieldsSelected =
        selection.fields.some((f) => isInCompanyFolder(f.name)) ||
        existingMappedFields.some((name) => isInCompanyFolder(name));
      const isCompanyContact =
        selection.components[roleComponentIndex].name === "company/contact";

      /**
       * Disabled if:
       * 1. In a Person folder but a company field is selected or
       * 2. In a Company folder but a person field is selected or
       * 3. A non-contact mapped field is selected in a group other than "layoutContact".
       * 4. Is a Company folder and is mapping company contact because
       * company contact can only be a person
       */
      return (
        (isInPersonFolder(folder.name) && isCompanyMappedFieldsSelected) ||
        (isInCompanyFolder(folder.name) && isPersonMappedFieldsSelected) ||
        (existingMappedFields.length > 0 && isOrdinaryGroup) ||
        (isInCompanyFolder(folder.name) && isCompanyContact)
      );
    }

    // Check for normal group of role provider fields
    if (isRoleProviderLayout && !!group) {
      const isPersonMappedFieldsSelected =
        selection.fields.some((f) => isInPersonFolder(f.name)) ||
        !!fixedSelection?.prefix.includes(RoleFolders.Person);
      const isCompanyMappedFieldsSelected =
        selection.fields.some((f) => isInCompanyFolder(f.name)) ||
        !!fixedSelection?.prefix.includes(RoleFolders.Company);
      const isTrustMappedFieldsSelected =
        selection.fields.some((f) => isInTrustFolder(f.name)) ||
        !!fixedSelection?.prefix.includes(RoleFolders.Trust);

      return (
        (isInPersonFolder(folder.name) &&
          (isCompanyMappedFieldsSelected || isTrustMappedFieldsSelected)) ||
        (isInCompanyFolder(folder.name) &&
          (isPersonMappedFieldsSelected || isTrustMappedFieldsSelected)) ||
        (isInTrustFolder(folder.name) &&
          (isPersonMappedFieldsSelected || isCompanyMappedFieldsSelected))
      );
    }

    // If a mapped field is selected in a group other than "LayoutContact" group,
    // only Person/Company/Trust folders should be disabled.
    if (existingMappedFields.length > 0 && isOrdinaryGroup) {
      return (
        isInPersonFolder(folder.name) ||
        isInCompanyFolder(folder.name) ||
        isInTrustFolder(folder.name)
      );
    }

    return existingMappedFields.length === 0 ? false : !!fixedSelection;
  };

  const isCompanyContactDetailsPath =
    isContactDetailsGroup &&
    (fixedSelection?.prefix.includes(RoleFolders.Company) ||
      selection.prefix.includes(RoleFolders.Company) ||
      selection.components.some(
        (component) =>
          (component.type === "path" || component.type === "role") &&
          component.name === RoleFolders.Company,
      ));

  const shouldHideFolder = (folder: { name: string; type?: string }) => {
    if (!isCompanyContactDetailsPath) return false;
    // Company → Contact must not be available for contactDetails groups
    return (
      folder.name === "contact" ||
      folder.name === "company/contact" ||
      folder.name.endsWith("/contact")
    );
  };

  const { folders, checkboxes, crumbs } = useMemo(() => {
    if (!selection.layout) return { folders: [], checkboxes: [] };

    const sharedDetail = fields[0]?.details?.split("/") || [];
    while (
      sharedDetail?.length &&
      !fields.every((f) => f.details?.startsWith(sharedDetail.join("/")))
    ) {
      sharedDetail.pop();
    }

    let paths: string[] = [];
    selection.components.forEach((c) => {
      if (c.type === "path") {
        paths.push(c.name);
      } else {
        paths = [];
      }
    });

    // Remove shared details which are already in the fixed selection components
    const fixedComponents = fixedSelection
      ? [...fixedSelection.components]
      : [];
    const fixedPathComponents =
      fixedComponents.filter((c) => c.type === "path") || [];
    while (
      fixedPathComponents.length &&
      fixedPathComponents[0].name === sharedDetail[0]
    ) {
      sharedDetail.shift();
      fixedPathComponents.shift();
    }

    const fullPrefix = [...sharedDetail, ...paths].filter(Boolean).join("/");
    const availableFields = fields.filter(
      (f) =>
        f.details?.startsWith(fullPrefix) && !hideField(selection?.layout, f),
    );
    const [inFields, inFolders] = partition(
      availableFields,
      (f) => f.details === fullPrefix,
    );
    const [roleFields, regularFields] = partition(
      inFields,
      (f) => f.type === "Role",
    );

    const checkIfFolderShareDetailsWithRoleFields = (folder: MatterField) =>
      roleFields.some((r) => r.details && folder.details?.startsWith(r.name));

    /**
     * Filter out fields which share same details with role fields.
     * Those fields will be combined with Role's fields later.
     */
    const regularInFolders = inFolders.filter(
      (f) => !checkIfFolderShareDetailsWithRoleFields(f),
    );

    const folderPaths = unique(
      regularInFolders
        .map(
          (f) =>
            f.details?.replace(new RegExp(`^${fullPrefix}/`), "").split("/")[0],
        )
        .filter(isNotNullOrUndefined),
    );
    const folderComponents = folderPaths.map((path) => ({
      name: path,
      displayName: path,
      type: "path",
      originalName: undefined,
    }));
    const roleFieldComponents = (isRoleComponentSelected ? [] : roleFields).map(
      (r) => ({
        name: r.name,
        displayName: r.displayName || r.name,
        type: "role",
        originalName: r.originalName || undefined,
      }),
    );

    const visibleComponents = [...selection.components].filter(
      (component, index) =>
        component.type !== "path" ||
        component.type !== fixedComponents[index]?.type ||
        component.name !== fixedComponents[index]?.name ||
        index === fixedComponents.length - 1,
    );

    return {
      folders: [...folderComponents, ...roleFieldComponents],
      checkboxes: regularFields,
      crumbs: visibleComponents,
    };
  }, [
    fields,
    matterTypes,
    selection.layout,
    selection.components,
    fixedSelection?.components,
  ]);

  const canAddChildSubLayout =
    fixedSelection?.layout?.providerId === roleProviderId &&
    !fixedSelection.layout?.parentId &&
    layouts.some((l) => l.parentId && l.parentId === fixedSelection.layout?.id);

  return (
    <div className="flex flex-col overflow-hidden rounded bg-white flex-1">
      {loading && <ProgressBar overlay />}
      <div className="mb-[1.6rem]">
        <p>
          Select which fields you want to include to map back to the matter in
          Smokeball.
        </p>
        <p>Each field can only be mapped once.</p>
      </div>
      <PickerCrumbs
        crumbs={crumbs}
        fixedSelection={fixedSelection}
        setSelection={setSelection}
        isRoleComponentSelected={isRoleComponentSelected}
        roleComponentIndex={roleComponentIndex}
        isRoleProviderLayout={isRoleProviderLayout}
        selection={selection}
        isInGroup={!!group}
        canAddChildSubLayout={canAddChildSubLayout}
      />
      {!loading && (
        <div className="flex-1 flex flex-col overflow-y-auto px-[2.4rem] pt-[1.6rem] pb-[2.4rem]">
          <div className="flex flex-col gap-small" data-testid="picker-fields">
            {selection.components.length ? (
              <>
                {folders
                  .filter((folder) => !shouldHideFolder(folder))
                  .map((folder, folderIndex) => (
                    <PickerItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${folder.type}-${folderIndex}`}
                      text={toSentenceCase(folder.displayName)}
                      icon="solidFolder"
                      iconColor="text-yellow-450"
                      disabled={shouldFolderBeDisabled(folder)}
                      onClick={() =>
                        setSelection({
                          layout: selection.layout,
                          prefix: [...selection.prefix, folder.name],
                          fields: isRoleFolder ? selection.fields : [],
                          components: [
                            ...selection.components,
                            {
                              displayName: folder.displayName,
                              name: folder.name,
                              type: folder.type === "role" ? "role" : "path",
                              originalName: folder.originalName,
                            },
                          ],
                        })
                      }
                    />
                  ))}
                {checkboxes.map((field) => {
                  const existing = existingMappedFields.includes(field.name);
                  const canToggleExisting =
                    isContactDetailsGroup &&
                    groupMappedFieldNames.includes(field.name);
                  const isSelected = selection.fields.some(
                    (selected) => selected.name === field.name,
                  );

                  return (
                    <PickerItem
                      key={field.name}
                      text={toSentenceCase(field.displayName || "")}
                      icon={
                        fieldComponentIcons[mapFieldType(field) || "text"].name
                      }
                      iconBgColor={
                        fieldComponentIcons[mapFieldType(field) || "text"]
                          .bgColor
                      }
                      checked={isSelected || (existing && !canToggleExisting)}
                      disabled={existing && !canToggleExisting}
                      onClick={() => {
                        const updatedSelection = multiSelect
                          ? {
                              ...selection,
                              fields: isSelected
                                ? selection.fields.filter(
                                    (selected) => selected.name !== field.name,
                                  )
                                : [...selection.fields, field],
                            }
                          : {
                              ...selection,
                              fields: [field],
                            };
                        setSelection(updatedSelection);
                      }}
                    />
                  );
                })}
                {!fields.length &&
                  !loading &&
                  isLoadingMatterFields === false && (
                    <p className="mt-[5rem] text-center">
                      No mapped fields available for &quot;
                      {selection.layout?.displayName || "this selection"}&quot;.
                    </p>
                  )}
              </>
            ) : (
              layouts.map((layout) => (
                <PickerItem
                  key={[layout.parentId, layout.id].join("/")}
                  text={layout.displayName || layout.name}
                  disabled={shouldLayoutBeDisabled(layout)}
                  icon="solidFolder"
                  iconColor="text-yellow-450"
                  onClick={() =>
                    setSelection({
                      layout,
                      prefix: [],
                      fields: [],
                      components: [
                        ...selection.components,
                        {
                          displayName: layout.displayName || layout.name,
                          name: layout.name,
                          type: "layout",
                        },
                      ],
                    })
                  }
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
